/** Dependencies */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Grid } from '@mui/material';
import Pusher, { Channel } from 'pusher-js';

/** Components */
import OrderSettingModal from './widgets/OrderSettingModal';
import DelivererModal from './modals/DelivererModal';
import LastTransactionsModal from './modals/LastTransactionsModal';
import AddPoolModal from './modals/AddPoolModal';
import AddCourierModal from './modals/AddCourierModal';
import OrderRejectModal from './modals/OrderRejectModal';

/** Hooks */
import useOrders from '../../../hooks/useOrders';
import useProfile from '../../../hooks/useProfile';
import usePosOrder from '../../../hooks/usePosOrder';

/** Services */
import { GroupedDataColumnType } from '../../../services/api/orders/types';

/** Styles */
import OrdersTableHeader from './OrdersTable/OrdersTableHeader';
import OrdersTableContent from './OrdersTable/OrdersTableContent';
import OrderApproveModal from './modals/OrderApproveModal';
import CookingAlarm from './modals/CookingAlarm';

function Orders() {
  const {
    getGroupedOrders,
    setOrderStatus,
    lastOrderTransactions,
    groupedOrders,
    meta,
    fetchBranchSettings,
    setGroupedOrdersWithPusher,
  } = useOrders();

  const { addOrderTransactionPayment } = usePosOrder();
  const { meProfile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();

  let pusher: Pusher | null = null;
  let channel: Channel | null = null;
  let orderChannel: Channel | null = null;

  const { t } = useTranslation('orders');
  const [selectActive, setSelectActive] = useState(0);
  const [page, setPage] = useState(1);
  const [statusIds, setStatusIds] = useState<number[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [type, setType] = useState<number>(0);
  const [fastPaymentModal, setFastPaymentModal] = useState(false);

  const [orderId, setOrderId] = useState<string>('');
  const [delivererId, setDelivererId] = useState<string>('');
  const [statusId, setStatusId] = useState<number>(0);
  const [order, setOrder] = useState<GroupedDataColumnType>();
  const [openLastTransactions, setOpenLastTransactions] =
    useState<boolean>(false);

  const [isShowLastOrders, setIsShowLastOrders] = useState('');
  const [isShowAddress, setIsShowAddress] = useState('');
  const [delivererModal, setDelivererModal] = useState(false);
  const [addPoolModal, setAddPoolModal] = useState(false);
  const [addCourierModal, setAddCourierModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [approveModal, setApproveModal] = useState(false);
  const [cookingAlarm, setCookingAlarm] = useState(false);
  const [preparationAlarmValue, setPreparationAlarmValue] = useState<number>(0);

  const getStatusType = (status_id: number) => {
    return status_id === 1
      ? 'placed'
      : status_id === 2
      ? 'preparing'
      : status_id === 3
      ? 'ready'
      : status_id === 5
      ? 'picked_up'
      : status_id === 16
      ? 'completed'
      : 'cancelled';
  };

  const handlePrintTable = async (tableId: string) => {
    await addOrderTransactionPayment({
      order_transaction_id: tableId,
      payment_action: 3,
      payments: [
        {
          amount: 0,
          payment_type_id: 1,
          remainder_amount: 0,
        },
      ],
    });
    enqueueSnackbar(t('order.details.print_success'), {
      variant: 'success',
    });
  };

  const debouncedAndFilteredSearch = async (
    pageValue?: number,
    status_ids?: number[],
  ) => {
    try {
      setLoading(true);
      if (meProfile?.activeBranch?.day_id) {
        await getGroupedOrders(
          meProfile?.activeBranch?.day_id,
          selectActive === 0,
          selectActive === 1,
          selectActive === 2,
          pageValue || page,
          status_ids || null,
        );
      }
    } catch (err) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      if (meProfile?.activeBranch?.id) {
        try {
          const settings = await fetchBranchSettings(meProfile.activeBranch.id);

          setPreparationAlarmValue(
            settings?.data?.preparation_alarm_value ?? 0,
          );
          console.log('settings', settings);
        } catch (err) {
          enqueueSnackbar('common:unknownError' as string, {
            variant: 'error',
          });
        }
      }
    };

    fetchSettings();
  }, [meProfile?.activeBranch?.id]);

  const handlePlayAudio = () => {
    const audio = new Audio('/newTask.mp3');
    audio.play().catch(error => {
      console.error('Error playing audio:', error);
    });

    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
    }, 50000); // 50 seconds in milliseconds
  };

  const handleChange = async (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
    await debouncedAndFilteredSearch(value);
  };

  const handleSetStatus = async (
    table_id: string,
    status_id: number,
    isPickedUp?: boolean,
  ) => {
    if (!isPickedUp) await setOrderStatus(table_id, status_id);
    setTimeout(() => {
      debouncedAndFilteredSearch();
    }, 500);
  };

  const handleFilterSubmit = () => {
    debouncedAndFilteredSearch();
  };

  const messageEventHandler = async (data: any) => {
    await setGroupedOrdersWithPusher(data, selectActive);
  };

  useEffect(() => {
    if (meProfile?.activeBranch?.id) {
      const appKey = process.env.REACT_APP_PUSHER_APP_KEY || '';

      // @ts-ignore
      pusher = new Pusher(appKey, {
        cluster: 'eu',
      });
      const channelName = `orders-${meProfile?.activeBranch?.id}-${
        selectActive === 0
          ? 'active'
          : selectActive === 1
          ? 'completed'
          : 'canceled'
      }`;
      channel = pusher.subscribe(channelName);
      orderChannel = pusher.subscribe(`branch.${meProfile?.activeBranch?.id}`);

      channel.bind('orders-event', messageEventHandler);
      orderChannel.bind('order.transaction.created', r => refresh(r));
      orderChannel.bind('order.transaction.updated', refresh);

      pusher.subscribe('user.' + meProfile?.activeBranch?.id);

      return () => {
        if (channel) {
          channel.unbind('orders-event', messageEventHandler);
          // @ts-ignore
          pusher.unsubscribe(channelName);
        }
        if (pusher) {
          pusher.disconnect();
        }
      };
      return () => {
        if (orderChannel) {
          orderChannel.unbind('orders-event', refresh);
          // @ts-ignore
          orderChannel.unsubscribe(`branch.${meProfile?.activeBranch?.id}`);
        }
        if (pusher) {
          pusher.disconnect();
        }
      };
    }
  }, [meProfile?.activeBranch?.id]);

  useEffect(() => {
    debouncedAndFilteredSearch();
  }, [meProfile, selectActive]);

  useEffect(() => {
    if (selectActive === 0) {
      debouncedAndFilteredSearch(undefined, statusIds);
    }
  }, [statusIds]);

  const refresh = async (r): Promise<void> => {
    if (!r.company_approved_at) {
      await handlePlayAudio();
    }
    await debouncedAndFilteredSearch();
  };

  return (
    <Grid>
      <br />
      <br />
      <Grid
        sx={{
          backgroundColor: 'white',
          borderRadius: '20px',
          overflowX: 'auto',
        }}
      >
        <OrdersTableHeader
          selectActive={selectActive}
          setSelectActive={setSelectActive}
          setPage={setPage}
          statusIds={statusIds}
          setStatusIds={setStatusIds}
          refresh={refresh}
          setCookingAlarm={setCookingAlarm}
        />
        <OrdersTableContent
          selectActive={selectActive}
          page={page}
          handleChange={handleChange}
          handlePrintTable={handlePrintTable}
          handleSetStatus={handleSetStatus}
          setType={setType}
          setOrder={setOrder}
          setOrderId={setOrderId}
          setOpen={setOpen}
          setDelivererModal={setDelivererModal}
          setPreparationAlarmValue={setPreparationAlarmValue}
          preparationAlarmValue={preparationAlarmValue}
          setDelivererId={setDelivererId}
          setStatusId={setStatusId}
          setFastPaymentModal={setFastPaymentModal}
          setAddPoolModal={setAddPoolModal}
          setRejectModal={setRejectModal}
          setApproveModal={setApproveModal}
          setAddCourierModal={setAddCourierModal}
          setOpenLastTransactions={setOpenLastTransactions}
          isShowLastOrders={isShowLastOrders}
          setIsShowLastOrders={setIsShowLastOrders}
          isShowAddress={isShowAddress}
          setIsShowAddress={setIsShowAddress}
          groupedOrders={groupedOrders}
          meta={meta}
        />
      </Grid>
      <OrderSettingModal
        open={open}
        onClose={() => {
          setOpen(false);
          setOrderId('');
        }}
        handlePrintTable={handlePrintTable}
        orderId={order?.id || orderId}
        order={order}
        type={getStatusType(type)}
        handleSetStatus={handleSetStatus}
        handleFilterSubmit={handleFilterSubmit}
        fastPaymentModal={fastPaymentModal}
        setFastPaymentModal={setFastPaymentModal}
      />
      <DelivererModal
        open={delivererModal}
        onClose={() => {
          setDelivererModal(false);
          setOrderId('');
          setStatusId(0);
        }}
        orderId={orderId || order?.id || ''}
        setAddCourierModal={setAddCourierModal}
        activeDelivererId={order ? order?.deliverer?.id : delivererId}
        handleSetStatus={handleSetStatus}
        statusId={statusId || order?.status_id || 0}
      />
      <OrderRejectModal
        open={rejectModal}
        onClose={() => {
          setRejectModal(false);
          setOrderId('');
          setStatusId(0);
        }}
        orderId={orderId || order?.id || ''}
        setAddCourierModal={setAddCourierModal}
        activeDelivererId={order ? order?.deliverer?.id : delivererId}
        handleSetStatus={handleSetStatus}
        statusId={statusId || order?.status_id || 0}
      />
      <OrderApproveModal
        open={approveModal}
        onClose={() => {
          setApproveModal(false);
          setOrderId('');
          setStatusId(0);
        }}
        orderId={orderId || order?.id || ''}
        activeDelivererId={order ? order?.deliverer?.id : delivererId}
        handleSetStatus={handleSetStatus}
        statusId={statusId || order?.status_id || 0}
      />
      <AddCourierModal
        open={addCourierModal}
        onClose={() => {
          setAddCourierModal(false);
          setOrderId('');
          setStatusId(0);
          setIsShowLastOrders('');
        }}
        setDelivererModal={setDelivererModal}
        setAddPoolModal={setAddPoolModal}
        order={order}
        orderId={orderId || order?.id || ''}
        handleSetStatus={handleSetStatus}
        statusId={statusId || order?.status_id || 0}
      />
      <CookingAlarm
        open={cookingAlarm}
        onClose={() => {
          setCookingAlarm(false);
          setOrderId('');
          setStatusId(0);
          setIsShowLastOrders('');
        }}
        orderId={orderId || order?.id || ''}
        handleSetStatus={handleSetStatus}
        setPreparationAlarmValue={setPreparationAlarmValue}
        preparationAlarmValue={preparationAlarmValue}
        statusId={statusId || order?.status_id || 0}
      />
      <LastTransactionsModal
        open={openLastTransactions}
        onClose={() => {
          setOpenLastTransactions(false);
          setIsShowLastOrders('');
        }}
        isShowLastOrders={isShowLastOrders}
        item={order}
        lastOrderTransactions={lastOrderTransactions}
      />
      <AddPoolModal
        open={addPoolModal}
        onClose={() => {
          setAddPoolModal(false);
          setOrderId('');
          setStatusId(0);
        }}
        orderId={orderId || order?.id || ''}
        setAddCourierModal={setAddCourierModal}
        getGroupedOrders={getGroupedOrders}
        selectActive={selectActive} // Add this line
        page={page} // Add this line
        statusIds={statusIds} // Add this line
        meProfile={meProfile}
      />
    </Grid>
  );
}

export default Orders;
